// The offline copy of the household's list, kept in sync the way Leaflet and Feed Me do it:
//  • items, stores, sections and memories are cached in IndexedDB, so the app opens and works
//    in a store with no signal;
//  • changes apply at once on the device and go into an ordered outbox, replayed when the server
//    is reachable (records with changes in flight keep the local version);
//  • the server's change log is pulled incrementally (GET /api/v1/sync?since=…);
//  • an events socket says "something changed" while the app is open, and the app syncs before
//    it's hidden or closed.

import { useSyncExternalStore } from 'react';
import {
  categorize, displayName, itemKey, learnedCategories, parseEntry,
  type Category, type Entity, type Item, type Memory, type Store, type SyncPage, type User,
} from '@milk/shared';
import { ApiError, request } from '../lib/api';
import { authToken, apiBase, pref, setPref, webOrigin } from '../lib/config';
import { idb, useDatabase } from '../lib/idb';
import '../lib/native';

export interface OutboxOp {
  id: string;
  method: string;
  path: string;
  body?: unknown;
  /** Shown in "changes waiting" lists. */
  label: string;
  /** The record this change is about ("item:<id>"): local edits to it win until it's sent. */
  ref?: string;
  /** What the server answers with (to store the server's copy). */
  entity?: Entity;
  ts: number;
  attempts?: number;
}

export interface State {
  status: 'idle' | 'loading' | 'ready';
  user: User | null;
  items: Record<string, Item>;
  stores: Record<string, Store>;
  categories: Record<string, Category>;
  memories: Record<string, Memory>;
  /** Everything has been downloaded at least once. */
  complete: boolean;
  cursor: number;
  online: boolean;
  syncing: boolean;
  pending: number;
  lastSync: number | null;
  queued: string[];
  syncError: string | null;
  /** The store the list is showing (null: all stores). Remembered per device. */
  storeId: string | null;
}

const empty = { items: {}, stores: {}, categories: {}, memories: {} };
let state: State = {
  status: 'idle', user: null, ...empty, complete: false, cursor: 0, online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false, pending: 0, lastSync: null, queued: [], syncError: null, storeId: null,
};
const listeners = new Set<() => void>();
export const getState = () => state;
export function setState(p: Partial<State> | ((s: State) => Partial<State>)) {
  state = { ...state, ...(typeof p === 'function' ? p(state) : p) };
  listeners.forEach((l) => l());
}
/** Called after every state change (returns an unsubscribe). */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
export function useStore<T>(sel: (s: State) => T): T {
  return useSyncExternalStore((l) => { listeners.add(l); return () => { listeners.delete(l); }; }, () => sel(state), () => sel(state));
}

/** Toasts are shown by the UI; the store just announces them. */
export function toast(text: string, kind: 'info' | 'error' = 'info', action?: { label: string; run: () => void }) {
  window.dispatchEvent(new CustomEvent('milk:toast', { detail: { text, kind, action } }));
}

// ------------------------------------------------------------------ cache

const PREFIX: Record<Entity, string> = { item: 'i:', store: 's:', category: 'c:', memory: 'm:' };
const COLL: Record<Entity, 'items' | 'stores' | 'categories' | 'memories'> = { item: 'items', store: 'stores', category: 'categories', memory: 'memories' };
const idOf = (e: Entity, r: any): string => (e === 'memory' ? r.key : r.id);

let outbox: OutboxOp[] = [];

function dbNameFor(user: User) {
  let host = 'local';
  try { host = new URL(webOrigin()).host; } catch { /* keep */ }
  return `milk:${host}:${user.id}`;
}

/** Loads this person's offline copy, then syncs with the server. */
export async function startStore(user: User) {
  if (state.user?.id === user.id && state.status !== 'idle') return;
  useDatabase(dbNameFor(user));
  setState({ status: 'loading', user, ...empty, complete: false, cursor: 0 });
  const [items, stores, categories, memories, meta, ob] = await Promise.all([
    idb.getPrefix<Item>(PREFIX.item), idb.getPrefix<Store>(PREFIX.store), idb.getPrefix<Category>(PREFIX.category), idb.getPrefix<Memory>(PREFIX.memory),
    idb.get<{ cursor: number; complete: boolean; lastSync: number | null }>('meta'),
    idb.get<OutboxOp[]>('outbox'),
  ]);
  outbox = ob ?? [];
  const byId = <T>(list: T[], e: Entity) => Object.fromEntries(list.map((r) => [idOf(e, r), r])) as Record<string, T>;
  setState({
    status: 'ready', items: byId(items, 'item'), stores: byId(stores, 'store'), categories: byId(categories, 'category'), memories: byId(memories, 'memory'),
    cursor: meta?.cursor ?? 0, complete: !!meta?.complete, lastSync: meta?.lastSync ?? null, storeId: pref<string | null>('store', null),
    pending: outbox.length, queued: outbox.map((o) => o.label),
  });
  connectEvents();
  void flushOutbox().then(() => pull());
}

/** Sign-out: forget everything on this device for that account. */
export async function stopStore() {
  disconnectEvents();
  stopProbe();
  outbox = [];
  setState({ status: 'idle', user: null, ...empty, complete: false, cursor: 0, pending: 0, queued: [], lastSync: null });
  await idb.drop();
}

function saveMeta() {
  void idb.set('meta', { cursor: state.cursor, complete: state.complete, lastSync: state.lastSync });
}

/** Stores and removes records on this device (state + IndexedDB). */
export function putRecords(e: Entity, list: any[], removed: string[] = []) {
  if (!list.length && !removed.length) return;
  const coll = COLL[e];
  setState((s) => {
    const next = { ...(s[coll] as Record<string, unknown>) };
    for (const r of list) next[idOf(e, r)] = r;
    for (const id of removed) delete next[id];
    return { [coll]: next } as Partial<State>;
  });
  void idb.batch(list.map((r) => [PREFIX[e] + idOf(e, r), r] as [string, unknown]), removed.map((id) => PREFIX[e] + id));
}

// ------------------------------------------------------------------ online state

export function setOnline(online: boolean) {
  if (online) stopProbe(); else startProbe();
  if (state.online === online) return;
  setState({ online });
  if (online) { connectEvents(); void flushOutbox().then(() => pull()); }
}

// While offline, poll the server: the browser's "online" event doesn't fire when the network was
// up but the server wasn't (NAS asleep, VPN not connected yet, VPS restarting).
let probeTimer: ReturnType<typeof setInterval> | null = null;
function startProbe() {
  if (probeTimer) return;
  probeTimer = setInterval(async () => {
    try { await request('GET', '/api/v1/health'); } catch { return; }
    setOnline(true);
  }, 10_000);
}
function stopProbe() {
  if (probeTimer) clearInterval(probeTimer);
  probeTimer = null;
}

// ------------------------------------------------------------------ outbox

async function saveOutbox() {
  setState({ pending: outbox.length, queued: outbox.map((o) => o.label) });
  await idb.set('outbox', outbox);
}

/** Server errors worth retrying instead of dropping the change. */
export function isTransient(e: unknown): boolean {
  return e instanceof ApiError && e.status !== 0 && (e.status >= 500 || e.status === 408 || e.status === 429);
}

const MAX_SERVER_ERRORS = 5;
let retryDelay = 2000;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleRetry() {
  if (retryTimer) return;
  retryTimer = setTimeout(() => { retryTimer = null; void flushOutbox(); }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, 60_000);
}

const inflight = new Map<string, number>();
/** A change to this record ("item:<id>") hasn't reached the server yet. */
export function hasPending(ref: string): boolean {
  return (inflight.get(ref) ?? 0) > 0 || outbox.some((o) => o.ref === ref);
}

/** Stores what the server answered (its copy of the record; a merged duplicate replaces ours). */
function applyResult(entity: Entity | undefined, r: any) {
  if (!entity || !r || typeof r !== 'object') return;
  if (entity === 'item' && typeof r.id === 'string' && typeof r.categoryId === 'string') {
    const { mergedFrom, ...item } = r;
    putRecords('item', [item], mergedFrom && mergedFrom !== item.id ? [mergedFrom] : []);
  } else if (entity === 'store' && typeof r.id === 'string' && Array.isArray(r.aisleOrder)) putRecords('store', [r]);
  else if (entity === 'category' && typeof r.id === 'string' && typeof r.position === 'number') putRecords('category', [r]);
  else if (entity === 'memory' && typeof r.key === 'string') putRecords('memory', [r]);
}

/**
 * Applies a change on the device at once and sends it to the server — now if possible, otherwise
 * from the outbox, in order, as soon as the server is reachable.
 */
export async function mutate<T>(op: Omit<OutboxOp, 'id' | 'ts'>, opts: { optimistic?: () => void; rollback?: () => void } = {}): Promise<T | undefined> {
  opts.optimistic?.();
  const queue = async () => {
    outbox.push({ ...op, id: crypto.randomUUID(), ts: Date.now() });
    await saveOutbox();
  };
  if (!state.online || outbox.length > 0) {
    await queue();
    if (state.online) void flushOutbox();
    return undefined;
  }
  const key = op.ref ?? '';
  inflight.set(key, (inflight.get(key) ?? 0) + 1);
  try {
    const r = await request<T>(op.method, op.path, op.body);
    applyResult(op.entity, r);
    return r;
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) { setOnline(false); await queue(); return undefined; }
    if (isTransient(e)) { await queue(); scheduleRetry(); return undefined; }
    opts.rollback?.();
    toast(e instanceof Error ? e.message : String(e), 'error');
    void pull();
    return undefined;
  } finally {
    const n = (inflight.get(key) ?? 1) - 1;
    if (n > 0) inflight.set(key, n); else inflight.delete(key);
  }
}

let flushRun: Promise<void> | null = null;
/** Replays the outbox; a call while one is running joins it. */
export function flushOutbox(): Promise<void> {
  if (!flushRun) flushRun = replay().finally(() => { flushRun = null; });
  return flushRun;
}

async function replay() {
  if (!outbox.length || !state.online) return;
  setState({ syncing: true });
  let failed = 0;
  try {
    while (outbox.length) {
      const op = outbox[0];
      try {
        applyResult(op.entity, await request<any>(op.method, op.path, op.body));
      } catch (e) {
        if (e instanceof ApiError && e.status === 0) { setOnline(false); return; }
        const serverError = e instanceof ApiError && e.status === 500;
        if (serverError && (op.attempts ?? 0) + 1 >= MAX_SERVER_ERRORS) {
          failed++;
          console.warn('Dropping a change the server keeps rejecting', op, e);
        } else if (isTransient(e) || (e instanceof ApiError && e.status === 401)) {
          if (serverError) { op.attempts = (op.attempts ?? 0) + 1; await saveOutbox(); }
          scheduleRetry();
          return;
        } else if (!(e instanceof ApiError && e.status === 404)) {
          // refused for good (e.g. a store deleted meanwhile): the server's copy wins.
          // 404s are quiet: someone else already removed it, which is what we wanted anyway.
          failed++;
          console.warn('Dropping an offline change the server refused', op, e);
        }
      }
      outbox.shift();
      await saveOutbox();
    }
    retryDelay = 2000;
    if (failed) toast(`${failed} offline change${failed > 1 ? 's' : ''} couldn't be saved`, 'error');
  } finally {
    setState({ syncing: false });
    if (failed) void pull();
  }
}

// ------------------------------------------------------------------ pulling changes

let pullRun: Promise<void> | null = null;
let pullAgain = false;
/** Downloads what changed on the server since the last sync. */
export function pull(): Promise<void> {
  if (pullRun) { pullAgain = true; return pullRun; }
  pullRun = (async () => {
    do {
      pullAgain = false;
      await pullOnce();
    } while (pullAgain);
  })().finally(() => { pullRun = null; });
  return pullRun;
}

async function pullOnce() {
  if (state.status !== 'ready' || !state.user) return;
  // queued changes aren't on the server yet: send them first so the server's copy includes them
  if (outbox.length) { await flushOutbox(); if (outbox.length) return; }
  const user = state.user;
  setState({ syncing: true });
  try {
    let cursor = state.cursor;
    for (let page = 0; page < 200; page++) {
      const r = await request<SyncPage>('GET', `/api/v1/sync?since=${cursor}&limit=500`);
      if (state.user !== user) return; // signed out meanwhile
      const keep = (e: Entity) => (x: any) => !hasPending(`${e}:${idOf(e, x)}`);
      putRecords('category', r.categories.filter(keep('category')));
      putRecords('store', r.stores.filter(keep('store')));
      putRecords('memory', r.memories.filter(keep('memory')));
      putRecords('item', r.items.filter(keep('item')));
      for (const e of ['item', 'store', 'category', 'memory'] as Entity[]) {
        putRecords(e, [], r.deleted.filter((d) => d.entity === e && !hasPending(`${e}:${d.id}`)).map((d) => d.id));
      }
      cursor = r.cursor;
      setState({ cursor });
      if (!r.hasMore) break;
    }
    if (state.storeId && !state.stores[state.storeId]) setStoreView(null);
    setState({ complete: true, lastSync: Date.now(), syncError: null });
    saveMeta();
    setOnline(true);
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) setOnline(false);
    else if (e instanceof ApiError && e.status === 401) window.dispatchEvent(new Event('milk:signed-out'));
    else setState({ syncError: `Couldn’t download the list: ${(e as Error).message}` });
  } finally {
    setState({ syncing: false });
  }
}

// ------------------------------------------------------------------ sync now (before suspend)

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
let syncRun: Promise<number> | null = null;

/** Sends everything local to the server, waiting at most `timeoutMs`. Resolves with the changes still waiting. */
export function syncNow(opts: { timeoutMs?: number; pull?: boolean } = {}): Promise<number> {
  if (syncRun) return syncRun;
  syncRun = (async () => {
    const work = (async () => {
      if (state.status !== 'ready') return;
      if (!state.online) {
        try { await request('GET', '/api/v1/health'); setOnline(true); } catch { return; }
      }
      await flushOutbox();
      if (opts.pull) await pull();
    })();
    await Promise.race([work.catch(() => {}), sleep(opts.timeoutMs ?? 8000)]);
    return outbox.length;
  })().finally(() => { syncRun = null; });
  return syncRun;
}

// ------------------------------------------------------------------ events socket

let socket: WebSocket | null = null;
let retry = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connecting = false;

function connectEvents() {
  if (state.status !== 'ready' || socket || connecting || typeof WebSocket === 'undefined') return;
  void openSocket();
}

async function openSocket() {
  connecting = true;
  let auth = '';
  if (authToken()) {
    try {
      const { ticket } = await request<{ ticket: string }>('POST', '/api/v1/events/ticket', {});
      auth = `?ticket=${encodeURIComponent(ticket)}`;
    } catch {
      connecting = false;
      scheduleReconnect();
      return;
    }
  }
  const base = (apiBase() || window.location.origin).replace(/^http/, 'ws');
  let ws: WebSocket;
  try { ws = new WebSocket(`${base}/api/v1/events${auth}`); } catch { connecting = false; scheduleReconnect(); return; }
  connecting = false;
  socket = ws;
  let hb: ReturnType<typeof setInterval> | null = null;
  ws.onopen = () => {
    const wasDown = retry > 0 || !state.online;
    retry = 0;
    setOnline(true);
    if (wasDown) void pull();
    hb = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 30_000);
  };
  ws.onmessage = (e) => {
    if (e.data === 'pong') return;
    try {
      const m = JSON.parse(e.data as string) as { type: string; seq: number };
      if ((m.type === 'changed' || m.type === 'hello') && m.seq > state.cursor) void pull();
    } catch { /* ignore */ }
  };
  ws.onclose = (e) => {
    if (hb) clearInterval(hb);
    if (socket !== ws) return;
    socket = null;
    if (e.code === 4401) { window.dispatchEvent(new Event('milk:signed-out')); return; }
    scheduleReconnect();
  };
}

function scheduleReconnect() {
  if (reconnectTimer || state.status !== 'ready') return;
  retry++;
  if (retry > 1) setOnline(false);
  reconnectTimer = setTimeout(() => { reconnectTimer = null; connectEvents(); }, Math.min(30_000, 500 * 2 ** Math.min(retry, 6)));
}

function disconnectEvents() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  const s = socket;
  socket = null;
  s?.close();
}

// ------------------------------------------------------------------ views

export function setStoreView(storeId: string | null) {
  setPref('store', storeId);
  setState({ storeId });
}

/** Sections as they are on this device (for categorize). */
export function guessCategory(name: string): string {
  const known = new Set(Object.keys(state.categories));
  return categorize(name, { learned: learnedCategories(Object.values(state.memories)), known: known.size ? known : undefined }).categoryId;
}

/** The item with this name that's still to buy, if any. */
export function findOpenItem(name: string): Item | undefined {
  const key = itemKey(name);
  return Object.values(state.items).find((i) => !i.checked && itemKey(i.name) === key);
}

// ------------------------------------------------------------------ item actions

const now = () => new Date().toISOString();
const mergeStores = (a: string[], b: string[]) => (!a.length || !b.length ? [] : [...new Set([...a, ...b])]);

export interface AddResult { item: Item; merged: boolean }

/** Adds what was typed ("2 lbs ground beef"); if it's already on the list, updates that one instead. */
export function addItem(text: string, opts: { storeIds?: string[]; categoryId?: string; note?: string } = {}): AddResult | null {
  const parsed = parseEntry(text);
  const name = displayName(parsed.name);
  if (!itemKey(name)) return null;
  const storeIds = opts.storeIds ?? [];
  const dup = findOpenItem(name);
  if (dup) {
    const patch: Partial<Item> = {};
    if (parsed.quantity && parsed.quantity !== dup.quantity) patch.quantity = parsed.quantity;
    const merged = mergeStores(dup.storeIds, storeIds);
    if (merged.length !== dup.storeIds.length) patch.storeIds = merged;
    if (Object.keys(patch).length) updateItem(dup.id, patch);
    return { item: { ...dup, ...patch }, merged: true };
  }
  const id = crypto.randomUUID();
  const me = state.user!;
  const item: Item = {
    id, name, quantity: parsed.quantity, note: opts.note ?? '', categoryId: opts.categoryId ?? guessCategory(name), storeIds,
    checked: false, checkedAt: null, addedBy: me.id, addedByName: me.name, createdAt: now(), updatedAt: now(),
  };
  void mutate({
    method: 'POST', path: '/api/v1/items', entity: 'item', ref: `item:${id}`, label: `Add ${name}`,
    body: { id, name, quantity: item.quantity, note: item.note, categoryId: item.categoryId, storeIds },
  }, { optimistic: () => putRecords('item', [item]), rollback: () => putRecords('item', [], [id]) });
  return { item, merged: false };
}

export type ItemPatch = Partial<Pick<Item, 'name' | 'quantity' | 'note' | 'categoryId' | 'storeIds' | 'checked'>>;

export function updateItem(id: string, p: ItemPatch) {
  const before = state.items[id];
  if (!before) return;
  const next: Item = { ...before, ...p, updatedAt: now() };
  if (p.name !== undefined) {
    next.name = displayName(p.name);
    if (p.categoryId === undefined && itemKey(next.name) !== itemKey(before.name)) next.categoryId = guessCategory(next.name);
  }
  if (p.checked !== undefined) next.checkedAt = p.checked ? now() : null;
  const body: Record<string, unknown> = { ...p };
  if (next.categoryId !== before.categoryId) body.categoryId = next.categoryId;
  // moving it teaches everyone where it goes (the server does the same; this makes it work offline)
  const key = itemKey(next.name);
  const memBefore = state.memories[key];
  const learns = p.categoryId !== undefined && p.categoryId !== before.categoryId;
  const label = p.checked !== undefined ? `${p.checked ? 'Check off' : 'Uncheck'} ${before.name}` : learns ? `Move ${before.name}` : `Edit ${before.name}`;
  void mutate({ method: 'PATCH', path: `/api/v1/items/${id}`, body, entity: 'item', ref: `item:${id}`, label }, {
    optimistic: () => {
      putRecords('item', [next]);
      if (learns) {
        const base: Memory = memBefore ?? { key, name: next.name, categoryId: null, timesBought: 0, lastBoughtAt: null, createdAt: now(), updatedAt: now() };
        putRecords('memory', [{ ...base, categoryId: p.categoryId!, updatedAt: now() }]);
      }
    },
    rollback: () => { putRecords('item', [before]); if (learns) putRecords('memory', memBefore ? [memBefore] : [], memBefore ? [] : [key]); },
  });
}

export function toggleItem(id: string) {
  const it = state.items[id];
  if (it) updateItem(id, { checked: !it.checked });
}

/** Removes it (no memory is made). Returns a function that puts it back. */
export function deleteItem(id: string): (() => void) | null {
  const before = state.items[id];
  if (!before) return null;
  void mutate({ method: 'DELETE', path: `/api/v1/items/${id}`, ref: `item:${id}`, label: `Remove ${before.name}` },
    { optimistic: () => putRecords('item', [], [id]), rollback: () => putRecords('item', [before]) });
  return () => restoreItem(before);
}

function restoreItem(it: Item) {
  void mutate({
    method: 'POST', path: '/api/v1/items', entity: 'item', ref: `item:${it.id}`, label: `Put back ${it.name}`,
    body: { id: it.id, name: it.name, quantity: it.quantity, note: it.note, categoryId: it.categoryId, storeIds: it.storeIds },
  }, { optimistic: () => putRecords('item', [{ ...it, checked: false, checkedAt: null }]) });
}

/** Removes everything checked off that this device can see. */
export function clearChecked(): number {
  const ids = Object.values(state.items).filter((i) => i.checked).map((i) => i.id);
  if (!ids.length) return 0;
  void mutate({ method: 'POST', path: '/api/v1/items/clear-checked', body: { ids }, label: `Clear ${ids.length} checked` },
    { optimistic: () => putRecords('item', [], ids) });
  return ids.length;
}

// ------------------------------------------------------------------ stores

export function addStore(name: string): Store {
  const id = crypto.randomUUID();
  const order = Object.values(state.categories).sort((a, b) => a.position - b.position).map((c) => c.id);
  const s: Store = { id, name: name.trim(), aisleOrder: order, position: Object.keys(state.stores).length, location: null, wifiNames: [], beacons: [], createdAt: now(), updatedAt: now() };
  void mutate({ method: 'POST', path: '/api/v1/stores', body: { id, name: s.name, aisleOrder: order }, entity: 'store', ref: `store:${id}`, label: `Add ${s.name}` },
    { optimistic: () => putRecords('store', [s]), rollback: () => putRecords('store', [], [id]) });
  return s;
}

export type StorePatch = Partial<Pick<Store, 'name' | 'aisleOrder' | 'position' | 'wifiNames' | 'beacons'>> & { location?: { lat: number; lng: number; radiusM?: number } | null };

export function updateStore(id: string, p: StorePatch) {
  const before = state.stores[id];
  if (!before) return;
  const next: Store = {
    ...before, ...p, updatedAt: now(),
    location: p.location === undefined ? before.location : p.location && { lat: p.location.lat, lng: p.location.lng, radiusM: p.location.radiusM ?? before.location?.radiusM ?? 150 },
  };
  void mutate({ method: 'PATCH', path: `/api/v1/stores/${id}`, body: p, entity: 'store', ref: `store:${id}`, label: `Edit ${before.name}` },
    { optimistic: () => putRecords('store', [next]), rollback: () => putRecords('store', [before]) });
}

export function deleteStore(id: string) {
  const before = state.stores[id];
  if (!before) return;
  if (Object.keys(state.stores).length <= 1) { toast('Keep at least one store', 'error'); return; }
  const touched = Object.values(state.items).filter((i) => i.storeIds.includes(id));
  void mutate({ method: 'DELETE', path: `/api/v1/stores/${id}`, ref: `store:${id}`, label: `Delete ${before.name}` }, {
    optimistic: () => {
      putRecords('store', [], [id]);
      putRecords('item', touched.map((i) => ({ ...i, storeIds: i.storeIds.filter((s) => s !== id) })));
      if (state.storeId === id) setStoreView(null);
    },
    rollback: () => { putRecords('store', [before]); putRecords('item', touched); },
  });
}

// ------------------------------------------------------------------ sections

export function addCategory(name: string, emoji = '🏷️'): Category {
  const id = crypto.randomUUID();
  const maxPos = Math.max(0, ...Object.values(state.categories).filter((c) => c.id !== 'other').map((c) => c.position));
  const c: Category = { id, name: name.trim(), emoji, position: maxPos + 10, builtin: false, updatedAt: now() };
  void mutate({ method: 'POST', path: '/api/v1/categories', body: { id, name: c.name, emoji }, entity: 'category', ref: `category:${id}`, label: `Add section ${c.name}` },
    { optimistic: () => putRecords('category', [c]), rollback: () => putRecords('category', [], [id]) });
  return c;
}

export function updateCategory(id: string, p: { name?: string; emoji?: string }) {
  const before = state.categories[id];
  if (!before) return;
  void mutate({ method: 'PATCH', path: `/api/v1/categories/${encodeURIComponent(id)}`, body: p, entity: 'category', ref: `category:${id}`, label: `Rename ${before.name}` },
    { optimistic: () => putRecords('category', [{ ...before, ...p }]), rollback: () => putRecords('category', [before]) });
}

export function deleteCategory(id: string) {
  const before = state.categories[id];
  if (!before || before.builtin) return;
  const items = Object.values(state.items).filter((i) => i.categoryId === id);
  const mems = Object.values(state.memories).filter((m) => m.categoryId === id);
  void mutate({ method: 'DELETE', path: `/api/v1/categories/${encodeURIComponent(id)}`, ref: `category:${id}`, label: `Delete section ${before.name}` }, {
    optimistic: () => {
      putRecords('category', [], [id]);
      putRecords('item', items.map((i) => ({ ...i, categoryId: 'other' })));
      putRecords('memory', mems.map((m) => ({ ...m, categoryId: null })));
    },
    rollback: () => { putRecords('category', [before]); putRecords('item', items); putRecords('memory', mems); },
  });
}

// ------------------------------------------------------------------ memories

export function updateMemory(key: string, p: { categoryId?: string | null; name?: string }) {
  const before = state.memories[key];
  if (!before) return;
  void mutate({ method: 'PATCH', path: `/api/v1/memories/${encodeURIComponent(key)}`, body: p, entity: 'memory', ref: `memory:${key}`, label: `Edit memory ${before.name}` },
    { optimistic: () => putRecords('memory', [{ ...before, ...p, updatedAt: now() }]), rollback: () => putRecords('memory', [before]) });
}

/** Stops suggesting it. Returns a function that undoes the forgetting (on this device, until synced). */
export function forgetMemory(key: string) {
  const before = state.memories[key];
  if (!before) return;
  void mutate({ method: 'DELETE', path: `/api/v1/memories/${encodeURIComponent(key)}`, ref: `memory:${key}`, label: `Forget ${before.name}` },
    { optimistic: () => putRecords('memory', [], [key]), rollback: () => putRecords('memory', [before]) });
}

// ------------------------------------------------------------------ triggers

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => setOnline(true));
  window.addEventListener('offline', () => setOnline(false));
  window.addEventListener('pagehide', () => void syncNow({ timeoutMs: 2000 }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void syncNow();
    // back in front: catch up (the socket may have slept)
    else if (state.status === 'ready') { connectEvents(); void flushOutbox().then(() => pull()); }
  });
  if (window.MilkMaybe) window.MilkMaybe.sync = (timeoutMs?: number, withPull?: boolean) => syncNow({ timeoutMs, pull: withPull });
}
